import React, { useEffect, useState } from "react";
import { TextField, InputAdornment, Box, FormHelperText } from "@mui/material";

export interface PriceInputProps {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  error?: boolean;
  helperText?: string;
  size?: "small" | "medium";
}

const PriceInput: React.FC<PriceInputProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  required = false,
  fullWidth = true,
  error = false,
  helperText,
  size = "medium",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [hasFocus, setHasFocus] = useState(false);

  useEffect(() => {
    if (value === null || value === undefined) {
      setInputValue("");
    } else {
      const numericValue =
        typeof value === "number" ? value : parseFloat(String(value));

      if (!isNaN(numericValue)) {
        const formattedValue = numericValue.toFixed(3);
        setInputValue(
          formattedValue.replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1")
        );
      } else {
        setInputValue("");
      }
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const isValidFormat = /^\d*\.?\d{0,3}$/.test(rawValue) || rawValue === "";

    if (isValidFormat) {
      setInputValue(rawValue);
      if (rawValue === "" || rawValue === ".") {
        onChange(null);
      } else {
        const numericValue = parseFloat(rawValue);
        onChange(isNaN(numericValue) ? null : numericValue);
      }
    }
  };

  const handleBlur = () => {
    setHasFocus(false);
    if (inputValue && inputValue !== ".") {
      const numericValue = parseFloat(inputValue);
      if (!isNaN(numericValue)) {
        const formattedValue = numericValue
          .toFixed(3)
          .replace(/\.0+$/, "")
          .replace(/(\.\d*?)0+$/, "$1");
        setInputValue(formattedValue);
      }
    } else if (inputValue === ".") {
      setInputValue("");
      onChange(null);
    }
  };

  const handleFocus = () => {
    setHasFocus(true);
  };

  return (
    <Box sx={{ position: "relative", flex: fullWidth ? 1 : undefined }}>
      <TextField
        label={label}
        value={hasFocus ? inputValue : inputValue.replace(".", ",")}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        disabled={disabled}
        required={required}
        fullWidth={fullWidth}
        error={error}
        slotProps={{
          input: {
            endAdornment: <InputAdornment position="end">DT</InputAdornment>,
          },
        }}
        placeholder="0,000"
        size={size}
      />
      {helperText && (
        <FormHelperText error sx={{ position: "absolute", bottom: -22 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default PriceInput;
